/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  CTCEntity,
  Entity
} from '@ion/core/src/types/entities';
import {AuditRecordType} from '@ion/dls/src/editors/audit/AuditGridUtils';
import {
  assetAuditRecordSelector,
  assetAuditSelectedSelector,
  assetAuditShowChangesDialogSelector,
  assetAuditShowDialogSelector,
  assetEditSelectedSelector
} from '../admin/assets/selector';
import {auditAdminAsset, editAdminAsset} from '../admin/assets/state';
import {groupAudit} from '../admin/groups/state';
import {
  groupAuditRecordSelector,
  groupAuditSelectedSelector,
  groupAuditShowChangesDialogSelector,
  groupAuditShowDialogSelector,
  groupEditShowDialogSelector
} from '../admin/groups/state-selectors';
import {
  deviceAuditRecordSelector,
  deviceAuditSelectedSelector,
  deviceAuditShowChangesDialogSelector,
  deviceAuditShowDialogSelector
} from './selector';
import {auditDevices} from './state';

export const useDeviceDetails = () => {
  const dispatch = useDispatch();
  const showAuditDevice        = useSelector(deviceAuditShowDialogSelector);
  const auditDevice            = useSelector(deviceAuditSelectedSelector);
  const deviceAuditRecord      = useSelector(deviceAuditRecordSelector);
  const showAuditDeviceChanges = useSelector(deviceAuditShowChangesDialogSelector);
  const showAuditAsset         = useSelector(assetAuditShowDialogSelector);
  const auditAsset             = useSelector(assetAuditSelectedSelector);
  const assetAuditRecord       = useSelector(assetAuditRecordSelector);
  const showAuditAssetChanges  = useSelector(assetAuditShowChangesDialogSelector);
  const showEditGroup          = useSelector(groupEditShowDialogSelector);
  const showAuditGroup         = useSelector(groupAuditShowDialogSelector);
  const auditGroup             = useSelector(groupAuditSelectedSelector);
  const groupAuditRecord       = useSelector(groupAuditRecordSelector);
  const showAuditGroupChanges  = useSelector(groupAuditShowChangesDialogSelector);
  const editAssetSelected      = useSelector(assetEditSelectedSelector);
         
  const getAuditData = () => {
    let auditItem: CTCEntity;
    let auditEntity: Entity;
    let auditRecordData: AuditRecordType;
    if (showAuditAsset) {
      auditItem = auditAsset;
      auditEntity = Entity.asset;
    }
    if (showAuditDevice) {
      auditItem = auditDevice;
      auditEntity = Entity.device;
    }
    if (showAuditGroup) {
      auditItem = auditGroup;
      auditEntity = Entity.group;
    }
    if (showAuditAssetChanges) {
      auditRecordData = assetAuditRecord;
    }
    if (showAuditDeviceChanges) {
      auditRecordData = deviceAuditRecord;
    }
    if (showAuditGroupChanges) {
      auditRecordData = groupAuditRecord;
    }
    return {
      auditItem,
      auditEntity,
      auditRecordData
    };
  };

  const {auditItem, auditEntity, auditRecordData} = getAuditData();

  const onAuditClose = useCallback(() => {
    if (showAuditAsset) {
      dispatch(auditAdminAsset(null, false));
    } else if (showAuditDevice) {
      dispatch(auditDevices(null, false));
    } else if (showAuditGroup) {
      dispatch(groupAudit(null, false));
    }
  }, [showAuditAsset, showAuditDevice, showAuditGroup]);

  const onAssetEdit = useCallback(() => {
    dispatch(editAdminAsset(editAssetSelected, true, false));
  }, []);

  const onAssetAudit = useCallback(() => {
    dispatch(editAdminAsset(editAssetSelected, false, false));
  }, []);

  return {
    auditItem,
    auditEntity,
    auditRecordData,
    showAuditAsset,
    showAuditAssetChanges,
    showEditGroup,
    showAuditGroup,
    showAuditGroupChanges,
    onAuditClose,
    onAssetAudit,
    onAssetEdit
  };
};
